import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, Calendar, Users, GraduationCap, Building, Globe, FlaskConical, Calculator, Atom, Dna, TrendingUp, Briefcase, BookOpen, Feather, Scale, Leaf, Heart, Cpu } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import type { NewsItem, College, Stat, Notice } from '@/lib/index';
import { IMAGES } from '@/assets/images';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  FlaskConical,
  Calculator,
  Atom,
  Dna,
  TrendingUp,
  Briefcase,
  BookOpen,
  Feather,
  Scale,
  Leaf,
  Heart,
  Cpu,
  Calendar,
  Users,
  GraduationCap,
  Building,
  Globe,
};

const categoryColorMap: Record<string, string> = {
  '综合新闻': 'bg-primary/10 text-primary',
  '学术活动': 'bg-chart-1/20 text-chart-1',
  '通知公告': 'bg-destructive/10 text-destructive',
  '媒体南开': 'bg-chart-4/20 text-chart-4',
};

type ImageKey = keyof typeof IMAGES;

export function NewsCard({ item }: { item: NewsItem }) {
  const imgSrc = IMAGES[item.image as ImageKey] ?? item.image;
  const categoryClass = categoryColorMap[item.category] ?? 'bg-muted text-muted-foreground';

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    >
      <Card className="overflow-hidden h-full border border-border hover:border-ring transition-colors duration-200 group">
        <div className="relative overflow-hidden h-48">
          <img
            src={imgSrc}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <span className={`absolute top-3 left-3 text-xs font-medium px-2 py-1 rounded-full ${categoryClass}`}>
            {item.category}
          </span>
        </div>
        <CardContent className="p-4 flex flex-col gap-2">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {item.date}
            </span>
            {item.views !== undefined && (
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {item.views.toLocaleString()}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-card-foreground line-clamp-2 leading-snug text-sm group-hover:text-primary transition-colors">
            {item.title}
          </h3>
          <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
            {item.excerpt}
          </p>
          {item.author && (
            <p className="text-xs text-muted-foreground mt-auto pt-2 border-t border-border">
              {item.author}
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function CollegeCard({ college }: { college: College }) {
  const IconComponent = iconMap[college.icon];

  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.01 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    >
      <Card className="h-full border border-border hover:border-ring hover:shadow-[0_8px_30px_-6px_color-mix(in_srgb,var(--primary)_15%,transparent)] transition-all duration-300 group cursor-pointer">
        <CardContent className="p-6 flex flex-col gap-3">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
              {IconComponent ? (
                <IconComponent className="w-6 h-6 text-primary" />
              ) : (
                <div className="w-6 h-6 bg-primary/40 rounded" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-card-foreground text-base leading-tight group-hover:text-primary transition-colors">
                {college.name}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5 font-sans">
                {college.englishName}
              </p>
            </div>
            <Badge variant="secondary" className="text-xs flex-shrink-0">
              {college.category}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
            {college.description}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {college.disciplines.slice(0, 3).map((d) => (
              <span key={d} className="text-xs bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                {d}
              </span>
            ))}
            {college.disciplines.length > 3 && (
              <span className="text-xs text-muted-foreground px-1 py-0.5">
                +{college.disciplines.length - 3}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className="text-xs text-muted-foreground">
              专职教师 <span className="font-mono text-chart-1 font-medium">{college.facultyCount}</span> 人
            </span>
            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function useCountUp(target: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!start) return;
    const startTime = performance.now();
    const startValue = 0;

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(startValue + (target - startValue) * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [target, duration, start]);

  return count;
}

export function StatCard({ stat }: { stat: Stat }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const count = useCountUp(stat.value, 2200, visible);
  const IconComponent = iconMap[stat.icon];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="flex flex-col items-center gap-3 p-6 text-center">
      <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-1">
        {IconComponent ? (
          <IconComponent className="w-7 h-7 text-chart-1" />
        ) : (
          <div className="w-7 h-7 bg-chart-1/40 rounded" />
        )}
      </div>
      <div className="font-mono text-4xl font-bold text-chart-1 leading-none">
        {count.toLocaleString()}
        <span className="text-lg font-sans font-normal text-white/70 ml-1">{stat.unit}</span>
      </div>
      <div className="text-white font-semibold text-base">{stat.label}</div>
      <div className="text-white/60 text-xs">{stat.description}</div>
    </div>
  );
}

export function NoticeItem({ notice }: { notice: Notice }) {
  return (
    <Link
      to={notice.link}
      className="flex items-center gap-3 py-3 px-2 border-b border-border last:border-0 hover:bg-accent/50 rounded transition-colors group"
    >
      <span
        className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${notice.tagColor}`}
      >
        {notice.tag}
      </span>
      <span className="flex-1 text-sm text-card-foreground line-clamp-1 group-hover:text-primary transition-colors">
        {notice.title}
      </span>
      <span className="text-xs text-muted-foreground flex-shrink-0 font-mono">
        {notice.date.slice(5)}
      </span>
    </Link>
  );
}
